import React, { useEffect, useMemo, useState } from "react";
import {
  getStudyRowId,
  getStudyScheduleId,
  getStudySchedulePublishLabel,
  isGroupTypeUnavailable,
  SCHEDULE_STATUS_DRAFT,
  SCHEDULE_STATUS_PUBLISHED,
  studyHasScheduleSetup,
} from "./studyMgmtReportUtils";
import ScheduleClonePreview from "./ScheduleClonePreview";
import "./ScheduleStartChoiceModal.scss";
import "../isf/isfStudyPicker.scss";

function formatCloneableStudyLabel(study) {
  const number = String(study?.StudyNumber ?? study?.StudyCode ?? "").trim();
  const name = String(study?.StudyName ?? "").trim() || "Untitled study";
  return { number: number || "—", name };
}

function studySelectionKey(study) {
  const studyId = getStudyRowId(study);
  const scheduleId = getStudyScheduleId(study);
  return scheduleId || studyId;
}

function studyColor(study) {
  return study?.StudySchColor || study?.StudyColor || "#1e3a5f";
}

function ScheduleStatusBadge({ status }) {
  const isPublished = status === SCHEDULE_STATUS_PUBLISHED;
  return (
    <span
      className={`study-mgmt-report__schedule-badge ${
        isPublished ? "is-published" : "is-draft"
      }`}
    >
      {isPublished ? "Published" : "Draft"}
    </span>
  );
}

export default function ScheduleStartChoiceModal({
  open,
  targetStudy,
  cloneableStudies = [],
  scheduleStatusByStudyId = {},
  loading = false,
  error = "",
  organizationId = "",
  createdBy = "",
  onClose,
  onStartFromScratch,
  onClone,
}) {
  const [step, setStep] = useState("choice");
  const [search, setSearch] = useState("");
  const [selectedSourceId, setSelectedSourceId] = useState("");

  useEffect(() => {
    if (!open) {
      setStep("choice");
      setSearch("");
      setSelectedSourceId("");
    }
  }, [open]);

  const filteredStudies = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return cloneableStudies;
    return cloneableStudies.filter((study) => {
      const { number, name } = formatCloneableStudyLabel(study);
      const groupType = String(study?.GroupType ?? "").trim();
      return (
        number.toLowerCase().includes(query) ||
        name.toLowerCase().includes(query) ||
        groupType.toLowerCase().includes(query)
      );
    });
  }, [cloneableStudies, search]);

  useEffect(() => {
    if (step !== "clone" || selectedSourceId || !filteredStudies.length) return;
    setSelectedSourceId(studySelectionKey(filteredStudies[0]));
  }, [filteredStudies, selectedSourceId, step]);

  const selectedSourceStudy = useMemo(
    () =>
      cloneableStudies.find((study) => studySelectionKey(study) === selectedSourceId) ||
      null,
    [cloneableStudies, selectedSourceId],
  );

  if (!open) return null;

  const targetLabel =
    String(targetStudy?.StudyNumber ?? "").trim() ||
    String(targetStudy?.StudyName ?? "").trim() ||
    "this study";

  const isCloneStep = step === "clone";

  return (
    <div
      className={`modal fade show d-block schedule-start-choice-modal${
        isCloneStep ? " schedule-start-choice-modal--clone schedule-clone-workspace-modal" : ""
      }`}
      style={isCloneStep ? undefined : { backgroundColor: "rgba(0,0,0,0.5)" }}
      role="dialog"
      aria-modal="true"
      aria-labelledby="schedule-start-choice-title"
    >
      <div
        className={`modal-dialog ${
          isCloneStep
            ? "schedule-clone-workspace-modal__dialog"
            : "modal-dialog-centered"
        }`}
      >
        <div className={`modal-content${isCloneStep ? " schedule-clone-workspace-modal__content" : ""}`}>
          <div className="modal-header border-0 pb-2">
            <h5 className="modal-title" id="schedule-start-choice-title">
              <i className="fas fa-calendar-alt me-2 text-secondary" />
              {isCloneStep
                ? `Clone schedule into ${targetLabel}`
                : `Start schedule for ${targetLabel}`}
            </h5>
            <button
              type="button"
              className="btn-close"
              onClick={onClose}
              disabled={loading}
              aria-label="Close"
            />
          </div>

          <div className={`modal-body${isCloneStep ? " schedule-start-choice-modal__clone-body" : ""}`}>
            {step === "choice" ? (
              <>
                <p className="schedule-start-choice-modal__intro">
                  This study does not have a schedule yet. Choose how you want to begin.
                </p>
                <div className="schedule-start-choice-modal__choices">
                  <button
                    type="button"
                    className="schedule-start-choice-modal__choice"
                    onClick={onStartFromScratch}
                    disabled={loading}
                  >
                    <span className="schedule-start-choice-modal__choice-icon" aria-hidden="true">
                      <i className="fas fa-file" />
                    </span>
                    <span className="schedule-start-choice-modal__choice-body">
                      <div className="schedule-start-choice-modal__choice-title">
                        Start from scratch
                      </div>
                      <p className="schedule-start-choice-modal__choice-hint">
                        Open the schedule editor and build a new setup step by step.
                      </p>
                    </span>
                  </button>

                  <button
                    type="button"
                    className="schedule-start-choice-modal__choice"
                    onClick={() => setStep("clone")}
                    disabled={loading || cloneableStudies.length === 0}
                  >
                    <span className="schedule-start-choice-modal__choice-icon" aria-hidden="true">
                      <i className="fas fa-copy" />
                    </span>
                    <span className="schedule-start-choice-modal__choice-body">
                      <div className="schedule-start-choice-modal__choice-title">
                        Clone existing schedule
                      </div>
                      <p className="schedule-start-choice-modal__choice-hint">
                        Copy setup, visits, planner, payment, cost, e-communication, and documents
                        from another study. The cloned schedule stays in draft until you publish.
                      </p>
                    </span>
                  </button>
                </div>
                {cloneableStudies.length === 0 ? (
                  <p className="schedule-start-choice-modal__intro mb-0">
                    No other studies with a schedule are available to clone from.
                  </p>
                ) : null}
              </>
            ) : (
              <div className="schedule-start-choice-modal__clone-layout">
                <aside className="schedule-start-choice-modal__clone-sidebar isf-study-picker__list-pane">
                  <p className="schedule-start-choice-modal__clone-hint">
                    Choose a source study — preview updates on the right.
                  </p>
                  <div className="isf-study-picker__search-wrap">
                    <i className="fa fa-search isf-study-picker__search-icon" aria-hidden />
                    <input
                      type="search"
                      className="form-control isf-study-picker__search"
                      placeholder="Search studies…"
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      disabled={loading}
                    />
                  </div>
                  <div className="schedule-start-choice-modal__study-list-scroll">
                    <div className="isf-study-picker__list">
                    {filteredStudies.length ? (
                      filteredStudies.map((study) => {
                        const key = studySelectionKey(study);
                        const scheduleId = getStudyScheduleId(study);
                        const { number, name } = formatCloneableStudyLabel(study);
                        const groupType = String(study?.GroupType ?? "").trim() || "—";
                        const isSelected = selectedSourceId === key;
                        const scheduleStatus = getStudySchedulePublishLabel(
                          study,
                          scheduleStatusByStudyId,
                        );
                        const color = studyColor(study);
                        return (
                          <button
                            key={key}
                            type="button"
                            className={`isf-study-picker__item${isSelected ? " is-active" : ""}`}
                            onClick={() => setSelectedSourceId(key)}
                            disabled={loading || !scheduleId}
                          >
                            <span className="isf-study-picker__item-main">
                              <span className="isf-study-picker__item-head">
                                <span className="isf-study-picker__item-title">
                                  <span
                                    className="isf-study-picker__study-dot"
                                    style={{ backgroundColor: color }}
                                    aria-hidden
                                  />
                                  <span className="isf-study-picker__item-name">
                                    {number && number !== "—" ? (
                                      <span className="isf-study-picker__item-code">[{number}]</span>
                                    ) : null}
                                    <span className="isf-study-picker__item-study">{name}</span>
                                  </span>
                                </span>
                                <ScheduleStatusBadge status={scheduleStatus} />
                              </span>
                              <span className="isf-study-picker__item-meta schedule-start-choice-modal__study-meta-row">
                                <span className="isf-study-picker__item-pi">
                                  <i className="fa fa-layer-group" aria-hidden />
                                  {groupType}
                                </span>
                                <span
                                  className={`isf-study-picker__item-pi${
                                    !study?.PIName ? " isf-study-picker__item-pi--empty" : ""
                                  }`}
                                >
                                  <i className="fa fa-user-circle" aria-hidden />
                                  {study?.PIName || "No PI"}
                                </span>
                              </span>
                            </span>
                          </button>
                        );
                      })
                    ) : (
                      <p className="isf-study-picker__empty-list">No matching studies found.</p>
                    )}
                    </div>
                  </div>
                </aside>

                <section className="schedule-start-choice-modal__clone-preview">
                  <div className="schedule-start-choice-modal__clone-preview-head">
                    <h6>Schedule preview</h6>
                    {selectedSourceStudy ? (
                      <span className="schedule-start-choice-modal__clone-preview-label">
                        {formatCloneableStudyLabel(selectedSourceStudy).number} ·{" "}
                        {formatCloneableStudyLabel(selectedSourceStudy).name}
                      </span>
                    ) : null}
                  </div>
                  <div className="schedule-start-choice-modal__clone-preview-body">
                    <ScheduleClonePreview
                      sourceStudy={selectedSourceStudy}
                      organizationId={organizationId}
                      createdBy={createdBy}
                    />
                  </div>
                </section>
              </div>
            )}

            {error ? <div className="schedule-start-choice-modal__error">{error}</div> : null}
          </div>

          <div className="modal-footer border-0">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={onClose}
              disabled={loading}
            >
              Cancel
            </button>
            {step === "clone" ? (
              <button
                type="button"
                className="btn btn-primary"
                disabled={loading || !selectedSourceStudy}
                onClick={() => {
                  if (selectedSourceStudy) onClone?.(selectedSourceStudy);
                }}
              >
                {loading ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status" />
                    Cloning…
                  </>
                ) : (
                  <>
                    <i className="fas fa-copy me-1" />
                    Clone schedule
                  </>
                )}
              </button>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}

export function buildCloneableStudies(
  studies = [],
  targetStudy = null,
  scheduleStatusByStudyId = {},
) {
  const targetId = getStudyRowId(targetStudy);
  return studies.filter((study) => {
    const studyId = getStudyRowId(study);
    if (!studyId || studyId === targetId) return false;
    if (!studyHasScheduleSetup(study)) return false;
    if (isGroupTypeUnavailable(study)) return false;
    if (!getStudyScheduleId(study)) return false;

    const status = getStudySchedulePublishLabel(study, scheduleStatusByStudyId);
    return status === SCHEDULE_STATUS_DRAFT || status === SCHEDULE_STATUS_PUBLISHED;
  });
}
